import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {environment} from '../../environments/environment';
import {FreeTrialModel, FreeTrialRequestModel} from "../models/free-trial.model";
import {AuthenticationService} from "../services/authentication.service";
import {OAuthService} from "angular-oauth2-oidc";

@Injectable()
export class FreeTrialDataSource {
    private readonly baseUrl = environment.backendApi + 'api/FreeTrial';

    constructor(private http: HttpClient,
                protected authenticationService: AuthenticationService,
                protected oAuthService: OAuthService,
    ) {
    }

    /**
     * Starts a free trial for the specified organization.
     * @param requestModel The FreeTrialRequestModel object.
     * @returns Observable of the API response.
     */
    startFreeTrial(requestModel: FreeTrialRequestModel): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/StartFreeTrial`, requestModel);
    }


    /**
     * Fetches all free trials.
     * @returns An Observable array of FreeTrialModel objects.
     */
    getAllFreeTrials(): Observable<FreeTrialModel[]> {
        return this.http.get<FreeTrialModel[]>(`${this.baseUrl}/GetAllFreeTrials`);
    }

    /**
     * Fetches free trials that are nearing expiration.
     * @returns An Observable array of FreeTrial objects.
     */
    getExpiringTrials(): Observable<FreeTrialModel[]> {
        return this.http.get<FreeTrialModel[]>(`${this.baseUrl}/GetExpiringTrials`);
    }

    /**
     * Fetches expired free trials.
     * @returns An Observable array of FreeTrial objects.
     */
    getExpiredTrials(): Observable<FreeTrialModel[]> {
        const headers = {
            Authorization: `Bearer ${this.oAuthService.getAccessToken()}`
        };
        return this.http.get<FreeTrialModel[]>(`${this.baseUrl}/GetExpiredTrials`, {headers});
    }


    /**
     * Transitions expired free trials to subscriptions.
     * @returns Observable of the API response.
     */
    transitionExpiredTrials(): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/TransitionExpiredTrials`, {});
    }

    /**
     * Opts out an organization from its free trial.
     * @param companyId The organization ID.
     * @returns Observable of the API response.
     */
    optOutFreeTrial(companyId: number): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/OptOutFreeTrial/${companyId}`, {});
    }

    /**
     * Get free for organization.
     * @param organizationId The organization ID.
     * @return Observable of FreeTrialModel or null if not found.
     * */
    getFreeTrialForOrganization(organizationId: number): Observable<FreeTrialModel> {
        return this.http.get<FreeTrialModel>(`${this.baseUrl}/GetFreeTrialForOrganization/${organizationId}`, {});
    }
}
